import { useSelector } from "react-redux";
import { transformErrorResponse } from "../../../shared/oversight-core/utils/transformErrorResponse";
import { selectAuth } from "../../features/auth/auth-slice";
import { rootApi } from "../apiManager";

export type FacetCardinality = "SINGLE" | "MULTI";

export interface ISiteFacetValue {
  id: string;
  label: string;
  archived: boolean;
}

export interface ISiteFacet {
  id: string;
  // Set for the five standard facets (SECTOR, IMPACT_AREA, ...), null for an organization's own.
  standardKey: string | null;
  name: string;
  cardinality: FacetCardinality;
  required: boolean;
  hiddenFromGuests: boolean;
  archived: boolean;
  values: ISiteFacetValue[];
}

// A site's values from one facet, by id; labels come from the organization's facets.
export interface ISiteTag {
  facetId: string;
  valueIds: string[];
}

export interface IUpdateSiteFacetValue {
  // Absent for a new value.
  id?: string;
  label: string;
  archived: boolean;
}

export const siteClassificationAPI = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    // The answer is filtered for guests, so it is cached per sign-in: the token is part of the key
    // (and only that), so a guest signing in after an admin never gets the admin's unfiltered list.
    getSiteFacets: builder.query<
      { siteFacets: ISiteFacet[] },
      { tenantId: string; token: string }
    >({
      transformErrorResponse,
      query({ tenantId }) {
        return { url: `admin/tenants/${tenantId}/site-facets`, method: "GET" };
      },
      providesTags: ["SiteFacets"],
    }),
    createSiteFacet: builder.mutation<
      ISiteFacet,
      {
        tenantId: string;
        name: string;
        cardinality: FacetCardinality;
        hiddenFromGuests: boolean;
      }
    >({
      transformErrorResponse,
      query({ tenantId, ...body }) {
        return {
          url: `admin/tenants/${tenantId}/site-facets`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["SiteFacets"],
    }),
    updateSiteFacet: builder.mutation<
      ISiteFacet,
      {
        tenantId: string;
        facetId: string;
        name: string;
        hiddenFromGuests: boolean;
        archived: boolean;
        values: IUpdateSiteFacetValue[];
      }
    >({
      transformErrorResponse,
      query({ tenantId, facetId, ...body }) {
        return {
          url: `admin/tenants/${tenantId}/site-facets/${facetId}`,
          method: "PUT",
          body,
        };
      },
      // Refetched on failure too: a refused save usually means someone else changed the facet.
      invalidatesTags: ["SiteFacets"],
    }),
    setSiteTags: builder.mutation<
      { siteTags: ISiteTag[] },
      { plantId: string; siteTags: ISiteTag[] }
    >({
      transformErrorResponse,
      query({ plantId, siteTags }) {
        return {
          url: `admin/generation-service-provider/${plantId}/tags`,
          method: "PUT",
          body: { siteTags },
        };
      },
      // The Sites list shows each site's sector, so a saved change drops its cached pages; a failed
      // save changed nothing, so they are kept.
      invalidatesTags: (_result, error) => (error ? [] : ["SolarPlantList"]),
    }),
  }),
});

export const {
  useGetSiteFacetsQuery,
  useCreateSiteFacetMutation,
  useUpdateSiteFacetMutation,
  useSetSiteTagsMutation,
} = siteClassificationAPI;

// The organization's facets for the signed-in user. Use this rather than useGetSiteFacetsQuery, so
// the cache entry belongs to the current sign-in.
export const useSiteFacets = (tenantId: string | undefined) => {
  const { token } = useSelector(selectAuth);
  return useGetSiteFacetsQuery(
    { tenantId: tenantId ?? "", token: token ?? "" },
    { skip: !tenantId || !token },
  );
};
